/**
 * Status Bar Component
 * Displays document processing state, document stats, language and Recent.
 * Gateway health lives in the Status module (#92).
 */

import { escapeHtml } from '../core/utilities/dom';
import { formatFileSize } from '../core/utilities/formatting';
import { isValidValue } from '../core/utilities/validation';
import { useUIStore } from '../stores/ui-store';
import { BaseComponent, type BaseComponentOptions } from './base-component';
import type RecentDocuments from './recent-documents';
import { logger } from '../core/logger';

// Type definitions
export type DocumentState = 'none' | 'loading' | 'loaded';

export interface DocumentStats {
  filename: string | null;
  words: number | null;
  sentences: number | null;
  paragraphs: number | null;
  characters: number | null;
  parseTime: number | null;
  fileSize: number | null;
}

export interface StatusBarOptions extends BaseComponentOptions {
  documentState?: DocumentState;
  /**
   * External host for document stats (#92 follow-up chrome cleanup): when
   * given, StatusBar renders stats there instead of its own internal
   * section. StatusBar stays the single owner of the rendering logic.
   */
  documentStatsContainer?: HTMLElement | null;
  /** External host for the language selector; see documentStatsContainer. */
  languageContainer?: HTMLElement | null;
}

class StatusBar extends BaseComponent {
  private documentState: DocumentState;
  private documentStats: DocumentStats;
  private documentSection: HTMLElement | null;
  private documentStatsContainer: HTMLElement | null;
  private languageSection: HTMLElement | null;
  private languageContainer: HTMLElement | null;
  private recentContainer: HTMLElement | null;
  private recentDocuments?: RecentDocuments;

  // External hosts (#92 follow-up chrome cleanup): when set, these win over
  // the internal footer sections during render(). See StatusBarOptions.
  private readonly useExternalDocumentStats: boolean;
  private readonly useExternalLanguage: boolean;
  private uiStoreUnsubscribe?: () => void;

  constructor(options: StatusBarOptions = {}) {
    super(options);
    this.documentState = options.documentState || 'none';

    this.documentStats = {
      filename: null,
      words: null,
      sentences: null,
      paragraphs: null,
      characters: null,
      parseTime: null,
      fileSize: null,
    };

    this.documentSection = null;
    this.useExternalDocumentStats = options.documentStatsContainer != null;
    this.documentStatsContainer = options.documentStatsContainer ?? null;
    this.languageSection = null;
    this.useExternalLanguage = options.languageContainer != null;
    this.languageContainer = options.languageContainer ?? null;
    this.recentContainer = null;
  }

  protected getDefaultCssClass(): string {
    return 'status-bar';
  }

  protected getTranslationFallbacks(): Record<string, string> {
    return {
      'statusBar.document.none': 'No document',
      'statusBar.document.loading': 'Loading...',
      'statusBar.document.loaded': 'Document loaded',
      'statusBar.language.label': 'Language',
    };
  }

  /**
   * Bind to pre-existing markup and initialize visual state.
   */
  protected override render(): void {
    if (!this.container) return;

    this.recentContainer = this.container.querySelector(
      `.${this.baseCssClass}__recent-container`
    );
    this.documentSection = this.container.querySelector(
      `.${this.baseCssClass}__section--document`
    );
    if (!this.useExternalDocumentStats) {
      this.documentStatsContainer = this.container.querySelector(
        `.${this.baseCssClass}__document-stats`
      );
    }
    this.languageSection = this.container.querySelector(
      `.${this.baseCssClass}__section--language`
    );
    if (!this.useExternalLanguage) {
      this.languageContainer = this.container.querySelector(
        `.${this.baseCssClass}__language-container`
      );
    }

    // Initialize state
    this.updateDocumentState();
  }

  /**
   * Set document state and update display
   * @param state - Document state: 'none', 'loading', 'loaded'
   * @deprecated Prefer using useUIStore.getState().setDocumentLoadingState() instead.
   * This method is kept for backward compatibility but will sync with the store.
   */
  setDocumentState(state: DocumentState): void {
    const validStates: DocumentState[] = ['none', 'loading', 'loaded'];
    if (!validStates.includes(state)) {
      throw new Error(
        `Invalid document state: ${state}. Must be 'none', 'loading', or 'loaded'`
      );
    }

    // Also update the store for consistency (if not already triggered by store)
    const currentStoreState = useUIStore.getState().documentLoadingState;
    if (currentStoreState !== state) {
      useUIStore.getState().setDocumentLoadingState(state);
    }

    this.documentState = state;
    this.updateDocumentState();
  }

  /**
   * Update document state display
   */
  updateDocumentState(): void {
    if (!this.documentStatsContainer) return;

    // The footer's document section may not exist at all (e.g. the slim
    // footer, #92 follow-up chrome cleanup) when stats render into an
    // external host instead. State-class toggling is then simply skipped.
    if (this.documentSection) {
      // Remove all state classes
      this.documentSection.classList.remove(
        `${this.baseCssClass}__section--none`,
        `${this.baseCssClass}__section--loading`,
        `${this.baseCssClass}__section--loaded`
      );

      // Add current state class
      this.documentSection.classList.add(
        `${this.baseCssClass}__section--${this.documentState}`
      );
    }

    // Update display based on state
    if (this.documentState === 'loaded' && this.documentStats.filename) {
      // Document is loaded with stats - show detailed stats
      this.renderDocumentStats();
    } else if (this.documentState === 'loaded') {
      // Document is loaded but stats not set yet - show "Document loaded"
      this.documentStatsContainer.innerHTML = `
        <span class="${this.baseCssClass}__simple-status">${this.t('statusBar.document.loaded')}</span>
      `;
    } else {
      // Show state message (none or loading)
      this.documentStatsContainer.innerHTML = `
        <span class="${this.baseCssClass}__simple-status">${this.t(`statusBar.document.${this.documentState}`)}</span>
      `;
    }
  }

  /**
   * Render document statistics in the status bar
   */
  renderDocumentStats(): void {
    if (!this.documentStatsContainer) return;

    const stats: string[] = [];

    // Add filename stat
    const filenameStat = this.renderFilenameStat();
    if (filenameStat) stats.push(filenameStat);

    // Add count stats (words, sentences, paragraphs, characters)
    stats.push(...this.renderCountStats());

    // Add performance stats (parse time, file size)
    stats.push(...this.renderPerformanceStats());

    this.documentStatsContainer.innerHTML = stats.join('');
  }

  /**
   * Render filename stat with XSS protection
   * @returns HTML for filename stat or null
   */
  renderFilenameStat(): string | null {
    if (!this.documentStats.filename) return null;

    const rawFilename = this.documentStats.filename;
    const displayFilename =
      rawFilename.length > 20 ? '...' + rawFilename.slice(-17) : rawFilename;

    // Escape HTML to prevent XSS attacks
    const safeFilename = escapeHtml(displayFilename);
    const safeTitle = escapeHtml(rawFilename);

    return `<span class="${this.baseCssClass}__stat ${this.baseCssClass}__stat--filename" title="${safeTitle}">📄 ${safeFilename}</span>`;
  }

  /**
   * Render count statistics (words, sentences, paragraphs, characters)
   * @returns Array of HTML stat elements
   */
  renderCountStats(): string[] {
    const stats: string[] = [];
    const countFields = [
      {
        key: 'words' as keyof DocumentStats,
        labelKey: 'statusBar.document.words',
      },
      {
        key: 'sentences' as keyof DocumentStats,
        labelKey: 'statusBar.document.sentences',
      },
      {
        key: 'paragraphs' as keyof DocumentStats,
        labelKey: 'statusBar.document.paragraphs',
      },
      {
        key: 'characters' as keyof DocumentStats,
        labelKey: 'statusBar.document.characters',
      },
    ];

    for (const field of countFields) {
      const value = this.documentStats[field.key];
      if (isValidValue(value)) {
        stats.push(
          `<span class="${this.baseCssClass}__stat">${this.t(field.labelKey)}: ${(value as number).toLocaleString()}</span>`
        );
      }
    }

    return stats;
  }

  /**
   * Render performance statistics (parse time, file size)
   * @returns Array of HTML stat elements
   */
  renderPerformanceStats(): string[] {
    const stats: string[] = [];

    // Add parse time
    if (isValidValue(this.documentStats.parseTime)) {
      stats.push(
        `<span class="${this.baseCssClass}__stat">${this.t('statusBar.document.parseTime')}: ${this.documentStats.parseTime!.toFixed(1)}${this.t('units.ms')}</span>`
      );
    }

    // Add file size
    if (isValidValue(this.documentStats.fileSize)) {
      const formattedSize = formatFileSize(
        this.documentStats.fileSize!,
        this.t.bind(this)
      );
      stats.push(
        `<span class="${this.baseCssClass}__stat">${this.t('statusBar.document.fileSize')}: ${formattedSize}</span>`
      );
    }

    return stats;
  }

  /**
   * Set document statistics
   * @param stats - Document statistics object
   */
  setDocumentStats(stats: Partial<DocumentStats>): void {
    this.documentStats = {
      filename: stats.filename !== undefined ? stats.filename : null,
      words: stats.words !== undefined ? stats.words : null,
      sentences: stats.sentences !== undefined ? stats.sentences : null,
      paragraphs: stats.paragraphs !== undefined ? stats.paragraphs : null,
      characters: stats.characters !== undefined ? stats.characters : null,
      parseTime: stats.parseTime !== undefined ? stats.parseTime : null,
      fileSize: stats.fileSize !== undefined ? stats.fileSize : null,
    };

    if (this.documentState === 'loaded') {
      this.renderDocumentStats();
    }
  }

  /**
   * Update UI text when language changes
   */
  override updateUILanguage(): void {
    this.updateDocumentState();

    // Re-render document stats with new translations if document is loaded
    if (this.documentState === 'loaded' && this.documentStats.filename) {
      this.renderDocumentStats();
    }

    // Update language label
    if (this.languageSection) {
      const label = this.languageSection.querySelector(
        `.${this.baseCssClass}__language-label`
      );
      if (label) {
        label.textContent = this.t('statusBar.language.label') + ':';
      }
    }

    // Update recent documents translations
    if (this.recentDocuments) {
      this.recentDocuments.updateUILanguage();
    }
  }

  /**
   * Get language container element for mounting language selector
   * @returns Language container element
   */
  getLanguageContainer(): HTMLElement | null {
    return this.languageContainer;
  }

  /**
   * Set recent documents component
   * @param recentDocuments - Recent documents component instance
   */
  setRecentDocuments(recentDocuments: RecentDocuments): void {
    if (!this.recentContainer) {
      logger.error('Recent documents container not found in status bar');
      return;
    }

    this.recentDocuments = recentDocuments;
  }

  /**
   * Get recent documents container element for mounting recent documents component
   * @returns Recent documents container element
   */
  getRecentContainer(): HTMLElement | null {
    return this.recentContainer;
  }

  /**
   * Subscribe to the UI store for the document loading state
   */
  subscribeToStore(): void {
    // Subscribe to UI store for document loading state changes
    if (!this.uiStoreUnsubscribe) {
      this.uiStoreUnsubscribe = useUIStore.subscribe((state, prevState) => {
        // Only update if document loading state actually changed
        if (state.documentLoadingState !== prevState.documentLoadingState) {
          this.documentState = state.documentLoadingState;
          this.updateDocumentState();
        }
      });

      // Initialize with current UI store state
      const uiState = useUIStore.getState();
      this.documentState = uiState.documentLoadingState;
      this.updateDocumentState();
      logger.debug('StatusBar subscribed to UI store');
    }
  }

  /**
   * Unsubscribe from all stores
   */
  unsubscribeFromStore(): void {
    if (this.uiStoreUnsubscribe) {
      this.uiStoreUnsubscribe();
      this.uiStoreUnsubscribe = undefined;
      logger.debug('StatusBar unsubscribed from UI store');
    }
  }

  /**
   * Destroy the status bar
   */
  override destroy(): void {
    this.unsubscribeFromStore();
    this.documentSection = null;
    this.languageSection = null;
    super.destroy();
  }
}

export default StatusBar;
